import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Archive } from "lucide-react";
import { toast } from "sonner";

const REC_LABEL = {
  archive: { text: "Recommended: Archive", cls: "text-red-600" },
  pause: { text: "Recommended: Pause & close the loop", cls: "text-amber-600" },
  keep_trying: { text: "Recommended: Keep trying", cls: "text-forest" },
};

export default function ArchiveDialog({ open, onOpenChange, customer, onDone }) {
  const [advice, setAdvice] = useState(null);
  const [loadingAdvice, setLoadingAdvice] = useState(false);
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setAdvice(null); setReason(""); setConfirmed(false);
      setLoadingAdvice(true);
      api.get(`/customers/${customer.id}/archive-advice`)
        .then((r) => {
          setAdvice(r.data);
          const grounds = (r.data.grounds || []).join("; ");
          setReason(grounds || "Unresponsive after multiple follow-ups");
        })
        .catch(() => setAdvice({ recommendation: "pause", grounds: [], advice: "Unable to fetch advice." }))
        .finally(() => setLoadingAdvice(false));
    }
  }, [open, customer.id]);

  const submit = async () => {
    if (!confirmed) { toast.error("Please approve before archiving"); return; }
    setSubmitting(true);
    try {
      const r = await api.post(`/customers/${customer.id}/archive`, { reason, confirmed: true });
      toast.success("Customer archived");
      onDone?.(r.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const rec = advice && (REC_LABEL[advice.recommendation] || REC_LABEL.pause);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Archive className="h-5 w-5 text-stone-600" /> Archive customer</DialogTitle>
          <DialogDescription>The copilot advises the grounds for archiving. Review and approve before archiving.</DialogDescription>
        </DialogHeader>

        {loadingAdvice ? (
          <div className="flex items-center gap-2 py-6 text-sm text-stone-500"><Loader2 className="h-4 w-4 animate-spin" /> Assessing this customer…</div>
        ) : advice && (
          <div className="space-y-3">
            <div className="rounded-lg border border-stone-200 bg-stone-50 p-3" data-testid="archive-advice">
              {rec && <div className={`text-sm font-semibold ${rec.cls}`}>{rec.text}</div>}
              <p className="mt-1 text-sm text-stone-600">{advice.advice}</p>
              {advice.grounds?.length > 0 && (
                <ul className="mt-2 list-disc pl-5 text-xs text-stone-500">
                  {advice.grounds.map((g, i) => <li key={i}>{g}</li>)}
                </ul>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Reason for archiving</Label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} data-testid="archive-reason" />
            </div>
            <label className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 cursor-pointer">
              <Checkbox checked={confirmed} onCheckedChange={setConfirmed} data-testid="archive-confirm-checkbox" className="mt-0.5" />
              <span className="text-sm text-red-700">I approve archiving this customer on the grounds above. They will move to the Dormant / Archived list.</span>
            </label>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="destructive" onClick={submit} disabled={submitting || !confirmed} data-testid="archive-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Archive customer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
